import { useEffect, useState } from "react";
import useCampaignsService from "../../services/CampaignsService";
import useUserService from "../../services/UserService";
import { UserSummary } from "../../types/User";
import PositiveButton from "../PositiveButton";
import NegativeButton from "../NegativeButton";

interface CampaignPreviewUsersListProps {
  campaignId: string;
}

const describeUser = (user: UserSummary) => {
  const name = [user.givenName, user.familyName].filter(Boolean).join(" ");
  return name || user.handle || user.nickname || user.email || user.id;
};

export default function CampaignPreviewUsersList({
  campaignId,
}: CampaignPreviewUsersListProps) {
  const [previewUsers, setPreviewUsers] = useState<UserSummary[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchResults, setSearchResults] = useState<UserSummary[] | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    getPreviewUsersForCampaign,
    addPreviewUserToCampaign,
    removePreviewUserFromCampaign,
  } = useCampaignsService();
  const { searchUsers } = useUserService();

  useEffect(() => {
    const fetchPreviewUsers = async () => {
      setIsLoading(true);
      try {
        setPreviewUsers(await getPreviewUsersForCampaign(campaignId));
      } catch (err) {
        console.error("Error fetching preview users:", err);
        setError("Could not load the test users for this campaign.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreviewUsers();
  }, [campaignId, getPreviewUsersForCampaign]);

  const runSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults(null);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const results = await searchUsers(searchTerm.trim());
      setSearchResults(results.items);
    } catch (err) {
      console.error("Error searching users:", err);
      setError("Could not search for users.");
    } finally {
      setIsSearching(false);
    }
  };

  const addUser = async (user: UserSummary) => {
    setBusyUserId(user.id);
    setError(null);

    try {
      await addPreviewUserToCampaign(campaignId, user.id);
      setPreviewUsers(await getPreviewUsersForCampaign(campaignId));
      setSearchResults(null);
      setSearchTerm("");
    } catch (err) {
      console.error("Error adding preview user:", err);
      setError(`Could not add ${describeUser(user)} as a test user.`);
    } finally {
      setBusyUserId(null);
    }
  };

  const removeUser = async (user: UserSummary) => {
    setBusyUserId(user.id);
    setError(null);

    try {
      await removePreviewUserFromCampaign(campaignId, user.id);
      setPreviewUsers((current) => current.filter((u) => u.id !== user.id));
    } catch (err) {
      console.error("Error removing preview user:", err);
      setError(`Could not remove ${describeUser(user)} as a test user.`);
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="flex justify-between items-center">
        <h3>Test Users</h3>
      </div>
      <p className="text-sm text-white/70">
        These users can see this campaign in the live app before it has been
        approved. Users who have campaign previews enabled on their profile can
        see every unapproved campaign and don't need to be listed here.
      </p>

      <div className="flex flex-row gap-2">
        <input
          type="text"
          value={searchTerm}
          placeholder="Search by name or email"
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              runSearch();
            }
          }}
          className="w-full px-3 py-2 border rounded text-black border-cyan-300/25"
        />
        <PositiveButton
          type="button"
          disabled={isSearching || !searchTerm.trim()}
          onClick={runSearch}
        >
          {isSearching ? "Searching..." : "Search"}
        </PositiveButton>
      </div>

      {error && (
        <div className="text-white bg-red-800 py-1 px-2 rounded-xl">{error}</div>
      )}

      {searchResults !== null && (
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <h4>Search results</h4>
            <NegativeButton
              type="button"
              onClick={() => {
                setSearchResults(null);
                setSearchTerm("");
              }}
            >
              Clear
            </NegativeButton>
          </div>
          {searchResults.length > 0 ? (
            <table className="table-auto w-full card border-spacing-2">
              <tbody>
                {searchResults.map((user) => {
                  const alreadyAdded = previewUsers.some((u) => u.id === user.id);
                  return (
                    <tr key={user.id}>
                      <td className="p-4">{describeUser(user)}</td>
                      <td className="p-4">{user.email}</td>
                      <td className="p-4 text-right">
                        <PositiveButton
                          type="button"
                          disabled={alreadyAdded || busyUserId === user.id}
                          onClick={() => addUser(user)}
                        >
                          {alreadyAdded ? "Added" : "Add"}
                        </PositiveButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p>No users matched that search.</p>
          )}
        </div>
      )}

      {isLoading ? (
        <p>Loading test users...</p>
      ) : previewUsers.length > 0 ? (
        <table className="table-auto w-full card border-spacing-2">
          <thead>
            <tr>
              <th className="font-normal text-left p-4">Name</th>
              <th className="font-normal text-left p-4">Email</th>
              <th className="font-normal text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {previewUsers.map((user) => (
              <tr key={user.id}>
                <td className="p-4">{describeUser(user)}</td>
                <td className="p-4">{user.email}</td>
                <td className="p-4 text-right">
                  <NegativeButton
                    type="button"
                    disabled={busyUserId === user.id}
                    onClick={() => removeUser(user)}
                  >
                    Remove
                  </NegativeButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No test users have been added to this campaign.</p>
      )}
    </div>
  );
}
